import { useSnackbarContext } from "@ameelio/ui";
import * as Sentry from "@sentry/react";
import type { TFunction } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

// Kept as a plain function (not tied to a hook) so it can also be called
// from the centralized query error link in client.ts, outside of a
// component's render.
export function buildApolloErrorMessages(t: TFunction): Record<string, string> {
  return {
    "The requesting visitor already has a meeting scheduled at this time.": t(
      "You already have a meeting scheduled at this time."
    ), // Tailored to visitor first-person experience
    "User with an active organization membership can't have personal contacts.":
      t(
        "Since you have an active organizational membership, you can't have personal contacts with this account. You can set up another account to use with personal contacts."
      ),
    "resident account not found": t(
      "We could not find an account with that username. Please check and try again."
    ),
    "resident account is inactive": t(
      "Your account is inactive. In order to use Ameelio Connect you must have an active account."
    ),
    "email not found": t(
      "We could not find an account with that email address. Please check and try again."
    ),
    "invalid credentials": t(
      "Your password is incorrect. Please check and try again."
    ),
    "password expired": t(
      'Your password has expired. Please use "Forgot password?" to reset it, then sign in again.'
    ),
    "too many attempts": t(
      "You have exceeded the allowed number of attempts. Please try again later."
    ),
    "staff cannot authenticate as a user": t(
      "Your account is not supported by this application."
    ),
    "Connect user already exists.": t(
      "You already have an Ameelio Connect account. Please sign in using that email and password."
    ),
    "User already exists.": t(
      "You already have an Ameelio account from Ameelio Mail. Please sign in using that email and password."
    ),
  };
}

type Alert = ReturnType<typeof useSnackbarContext>["alert"];

// Shared implementation behind useApolloErrorHandler (call sites) and the
// centralized query error link in client.ts. Reports to Sentry and shows a
// toast for errors that don't otherwise reach an error boundary.
export function reportApolloError(
  e: unknown,
  {
    alert,
    errorMessages,
  }: { alert: Alert; errorMessages: Record<string, string> }
) {
  if (import.meta.env.DEV) console.error(e);
  if (!(e instanceof Error)) return;

  if (!(e.message in errorMessages))
    Sentry.captureException(e, { level: "info" });
  alert(
    "error",
    e.message in errorMessages ? errorMessages[e.message] : e.message
  );
}

export default function useApolloErrorHandler() {
  const snackbarContext = useSnackbarContext();
  const { t } = useTranslation();

  // Placed inside the hook for correct translations
  const errorMessages = useMemo(() => buildApolloErrorMessages(t), [t]);

  return function handleApolloError(e: unknown) {
    reportApolloError(e, { alert: snackbarContext.alert, errorMessages });
  };
}
